/**
 * 撮影の流れ（Issue #692。設計 docs/live-match-flow.md）: 対戦の状態と撮影予定の列（ナビの「撮影」の「撮影予定」タブ `/shoot/schedule`）。
 * 状態を進めるのは P1 の席の問い合わせ（試合を選ぶ）と、補正サーバーに届いた録画の開始・停止。ここでできるのは
 * 列を並べる（撮り方の選択・順番・外す）・中止（対戦アイドルへ）・録画を停止（決着がつかないまま終えるとき）。5 秒ごとに見直す。
 * 今の対戦の試合には入力画面へ入るリンク（P1 / P2 の席・観客。どれも ?live=1 で今の対戦の録画を追う。Issue #744）。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/api';
import { MODE_LABELS, gameLabel, joinLinks, matchSummary, moveItem, toItems } from '../utils/liveMatch';

export default function LiveMatchPanel() {
  const [m, setM] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setM(await api.project('GetLiveMatch', {}));
      setError(null);
    } catch (e) {
      setError(String(e?.message ?? e));
    }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [load]);

  const call = async (action, params = {}) => {
    setBusy(true);
    try {
      await api.project(action, params);
      await load();
    } catch (e) {
      setError(String(e?.message ?? e));
    } finally {
      setBusy(false);
    }
  };
  const saveItems = (items) => call('SetLiveSchedule', { items });

  if (!m) return <div className="card" style={{ marginBottom: 16 }}>撮影の状態を読んでいます…{error ? `（${error}）` : ''}</div>;
  const items = toItems(m.schedule);
  const links = joinLinks(m);
  return (
    <div className="card" style={{ marginBottom: 16 }} data-testid="live-match-panel">
      <div className="card-title" style={{ marginBottom: 8 }}>撮影</div>
      <div style={{ marginBottom: 8 }} data-testid="live-match-summary">{matchSummary(m)}</div>
      {m.dropped ? <div role="alert" style={{ color: '#f99', marginBottom: 8 }}>録画落ち: {m.dropped}（撮り直すなら中止して選び直す）</div> : null}
      {m.notice ? <div role="status" style={{ color: '#ffb347', marginBottom: 8 }}>{m.notice}</div> : null}
      {links.length ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }} data-testid="live-match-join">
          {links.map((l) => <Link key={l.key} to={l.to} className="btn btn-sm btn-primary" data-testid={`live-match-join-${l.key}`}>{l.label}</Link>)}
        </div>
      ) : null}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {m.state === 'playing' && !m.decided
          ? <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={() => call('RequestLiveStop')}
              title="決着がつかないまま終えるとき。録画を停止の問い合わせを出す">録画を停止</button> : null}
        {m.state !== 'idle'
          ? <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={() => call('AbortLiveMatch')}
              title="対戦アイドルに戻す（試合は列に残る）">中止</button> : null}
      </div>
      <div className="card-meta" style={{ marginBottom: 4 }}>撮影予定の列（デュエル一覧の試合の行の「撮影予定に追加」で足す）</div>
      {items.length === 0 ? <div className="card-meta">空です</div> : (
        <ol style={{ margin: 0, paddingLeft: 20 }} data-testid="live-schedule">
          {m.schedule.map((s, i) => (
            <li key={s.id} style={{ marginBottom: 4 }}>
              <span style={{ marginRight: 8 }}>{gameLabel(s)}</span>
              <select value={s.mode} disabled={busy} aria-label="撮り方"
                onChange={(e) => saveItems(items.map((it, k) => (k === i ? { ...it, mode: e.target.value } : it)))}>
                {Object.entries(MODE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button type="button" className="btn btn-sm btn-secondary" disabled={busy || i === 0} onClick={() => saveItems(moveItem(items, i, -1))}>↑</button>
              <button type="button" className="btn btn-sm btn-secondary" disabled={busy || i === items.length - 1} onClick={() => saveItems(moveItem(items, i, 1))}>↓</button>
              <button type="button" className="btn btn-sm btn-secondary" disabled={busy} onClick={() => saveItems(items.filter((_, k) => k !== i))}>外す</button>
            </li>
          ))}
        </ol>
      )}
      {error ? <div role="status" style={{ color: '#f99', marginTop: 8 }}>{error}</div> : null}
    </div>
  );
}
